import { Head, useForm, router } from "@inertiajs/react"
import { Scale, Plus, Pencil, Trash2, Search } from "lucide-react"
import { useState, useMemo } from "react"
import { MemberLayout } from "../components/MemberLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"

const GC_CATEGORY_ORDER = { professional: 0, personal: 1, spiritual: 2 }

// Urgency is now a 1-10 integer. The filter narrows by band.
const GC_URGENCY_RANGES = {
  "1-3": [1, 3],
  "4-6": [4, 6],
  "7-10": [7, 10],
}

function gcCompareWithNullsLast(a, b, direction) {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (a < b) return direction === "asc" ? -1 : 1
  if (a > b) return direction === "asc" ? 1 : -1
  return 0
}
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { timeAgo } from "@/lib/time-ago"
import { CategoryBadge, UrgencyBadge } from "@/components/group-features/DiscussionBadges"

function GCCreateDialog({ open, onOpenChange, isCoach = false, groupMembers = [] }) {
  const form = useForm({
    description: "",
    options_weighed: "",
    specific_answer: "",
    category: "professional",
    urgency: 5,
    account_id: "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    form.post("/app/member/godly_councils", {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) { form.reset(); form.clearErrors() }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              New Godly Council Request
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Seek wisdom from your group on a decision you're facing.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <GCFormFields form={form} isCoach={isCoach} groupMembers={groupMembers} />
          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing ? "Creating..." : "Create Request"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function GCDetailDialog({ open, onOpenChange, gc, isCoach = false, onEdit, onDelete }) {
  if (!gc) return null

  const canManage = gc.canManage

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-display text-xl">
            Godly Council Request
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            By {gc.author.name} &middot; {timeAgo(gc.createdAt)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <div className="space-y-4 py-4 px-4 sm:px-0">
          <div className="flex flex-wrap gap-1.5">
            <CategoryBadge category={gc.category} />
            <UrgencyBadge urgency={gc.urgency} />
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap">{gc.description}</p>
            </div>
            {gc.optionsWeighed && (
              <div>
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-1">Options Weighed</p>
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{gc.optionsWeighed}</p>
              </div>
            )}
            {gc.specificAnswer && (
              <div>
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-1">Specific Answer Sought</p>
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{gc.specificAnswer}</p>
              </div>
            )}
          </div>
        </div>
        {canManage && (
          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); onEdit(gc) }}>
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => { onOpenChange(false); onDelete(gc) }}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </div>
          </ResponsiveDialogFooter>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function GCEditDialog({ open, onOpenChange, gc }) {
  const form = useForm({
    description: gc?.description || "",
    options_weighed: gc?.optionsWeighed || "",
    specific_answer: gc?.specificAnswer || "",
    category: gc?.category || "professional",
    urgency: gc?.urgency ?? 5,
  })

  if (!gc) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    form.patch(`/app/member/godly_councils/${gc.id}`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) { form.reset(); form.clearErrors() }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Edit Godly Council Request
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Update your request for group wisdom.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <GCFormFields form={form} />
          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing ? "Saving..." : "Save Changes"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function GCFormFields({ form, isCoach = false, groupMembers = [] }) {
  return (
    <div className="space-y-4 py-4 px-4 sm:px-0">
      {isCoach && groupMembers.length > 0 && form.data.account_id !== undefined && (
        <div className="space-y-2">
          <Label>Create for member (optional)</Label>
          <Select
            value={form.data.account_id || "self"}
            onValueChange={(v) => form.setData("account_id", v === "self" ? "" : v)}
          >
            <SelectTrigger className="w-full bg-white border-zinc-300">
              <SelectValue placeholder="Yourself" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="self">Myself (coach)</SelectItem>
              {groupMembers.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500">
            When set, the request is owned by the selected member but recorded as created by you.
          </p>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="gc-description">Description</Label>
        <Textarea id="gc-description" placeholder="Describe the decision or situation..." rows={3}
          className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
          value={form.data.description} onChange={(e) => form.setData("description", e.target.value)} />
        {form.errors.description && <p className="text-sm text-red-600">{form.errors.description}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="gc-options">Options You've Weighed</Label>
        <Textarea id="gc-options" placeholder="What options have you considered?" rows={3}
          className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
          value={form.data.options_weighed} onChange={(e) => form.setData("options_weighed", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="gc-answer">Specific Answer You're Seeking</Label>
        <Textarea id="gc-answer" placeholder="What specific guidance do you need?" rows={3}
          className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
          value={form.data.specific_answer} onChange={(e) => form.setData("specific_answer", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={form.data.category} onValueChange={(v) => form.setData("category", v)}>
            <SelectTrigger className="w-full bg-white border-zinc-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
              <SelectItem value="spiritual">Spiritual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Urgency (1-10)</Label>
          <Select value={String(form.data.urgency)} onValueChange={(v) => form.setData("urgency", Number(v))}>
            <SelectTrigger className="w-full bg-white border-zinc-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}

export default function GodlyCouncilsIndex({ currentUser, godlyCouncils = [], userGroupRole, groupMembers = [] }) {
  const isCoach = userGroupRole === "coach"
  const [createOpen, setCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState(0)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedGc, setSelectedGc] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editKey, setEditKey] = useState(0)
  const [editingGc, setEditingGc] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [urgencyFilter, setUrgencyFilter] = useState("all")
  const [memberFilter, setMemberFilter] = useState("all")
  // Default: newest submitted first (task #517). Shared across the four
  // group-feature lists.
  const [sortKey, setSortKey] = useState("createdAt")
  const [direction, setDirection] = useState("desc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    setSortKey("createdAt")
    setDirection("desc")
  }

  const memberOptions = useMemo(() => {
    const byId = new Map()
    godlyCouncils.forEach((g) => {
      if (g.author?.id != null && !byId.has(g.author.id)) {
        byId.set(g.author.id, g.author)
      }
    })
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [godlyCouncils])

  const filtered = useMemo(() => {
    let results = godlyCouncils
    if (categoryFilter !== "all") results = results.filter((g) => g.category === categoryFilter)
    if (urgencyFilter !== "all") {
      const [min, max] = GC_URGENCY_RANGES[urgencyFilter]
      results = results.filter((g) => g.urgency != null && g.urgency >= min && g.urgency <= max)
    }
    if (memberFilter !== "all") {
      results = results.filter((g) => String(g.author?.id) === memberFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter((g) => g.description.toLowerCase().includes(q))
    }
    const sorted = [...results].sort((a, b) => {
      let aVal
      let bVal
      if (sortKey === "createdAt") {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : null
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : null
      } else if (sortKey === "description") {
        aVal = a.description?.toLowerCase() || null
        bVal = b.description?.toLowerCase() || null
      } else if (sortKey === "category") {
        aVal = a.category != null ? GC_CATEGORY_ORDER[a.category] : null
        bVal = b.category != null ? GC_CATEGORY_ORDER[b.category] : null
      } else if (sortKey === "urgency") {
        aVal = a.urgency != null ? a.urgency : null
        bVal = b.urgency != null ? b.urgency : null
      } else if (sortKey === "author") {
        aVal = a.author?.name?.toLowerCase() || null
        bVal = b.author?.name?.toLowerCase() || null
      }
      return gcCompareWithNullsLast(aVal, bVal, direction)
    })
    return sorted
  }, [godlyCouncils, categoryFilter, urgencyFilter, memberFilter, searchQuery, sortKey, direction])

  const handleCreate = () => { setCreateKey((k) => k + 1); setCreateOpen(true) }
  const handleRowClick = (gc) => { setSelectedGc(gc); setDetailOpen(true) }
  const handleEdit = (gc) => { setEditingGc(gc); setEditKey((k) => k + 1); setEditOpen(true) }
  const handleDelete = () => {
    if (deleteTarget) {
      router.delete(`/app/member/godly_councils/${deleteTarget.id}`, {
        preserveScroll: true,
        onSuccess: () => setDeleteTarget(null),
      })
    }
  }

  const isEmpty = godlyCouncils.length === 0

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="Godly Council - Kingdom Factor" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Godly Council</h1>
            <p className="text-zinc-600 mt-1">
              Seek wisdom from your group on decisions you're facing.
            </p>
          </div>
          <Button variant="gradient" onClick={handleCreate} className="self-start sm:self-auto">
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>

        {isEmpty && (
          <div className="text-center py-12 text-zinc-500">
            <Scale className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No godly council requests yet.</p>
            <p className="text-sm mt-1">Ask your group for wisdom on a decision.</p>
          </div>
        )}

        {!isEmpty && (
          <>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input placeholder="Search godly council..." className="pl-9 bg-white border-zinc-300"
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-40 bg-white border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="spiritual">Spiritual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                <SelectTrigger className="w-full sm:w-36 bg-white border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Urgency</SelectItem>
                  <SelectItem value="1-3">Low (1-3)</SelectItem>
                  <SelectItem value="4-6">Medium (4-6)</SelectItem>
                  <SelectItem value="7-10">High (7-10)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={memberFilter} onValueChange={setMemberFilter}>
                <SelectTrigger className="w-full sm:w-44 bg-white border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  {memberOptions.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filtered.length === 0 ? (
              <p className="text-center py-8 text-zinc-500 text-sm">No matching requests.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="description" sortKey={sortKey} direction={direction} onSort={handleSort}>
                      Description
                    </SortableHeader>
                    <SortableHeader column="category" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-28">
                      Category
                    </SortableHeader>
                    <SortableHeader column="urgency" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-24">
                      Urgency
                    </SortableHeader>
                    <SortableHeader column="author" sortKey={sortKey} direction={direction} onSort={handleSort} className="hidden sm:table-cell">
                      Author
                    </SortableHeader>
                    <SortableHeader column="createdAt" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-20 text-right">
                      Date
                    </SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((gc) => (
                    <TableRow key={gc.id} className="cursor-pointer" onClick={() => handleRowClick(gc)}>
                      <TableCell className="whitespace-normal">
                        <span className="font-medium text-zinc-900 line-clamp-2">{gc.description}</span>
                      </TableCell>
                      <TableCell><CategoryBadge category={gc.category} /></TableCell>
                      <TableCell><UrgencyBadge urgency={gc.urgency} /></TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-zinc-600">{gc.author.name}</TableCell>
                      <TableCell className="text-right text-sm text-zinc-500">{timeAgo(gc.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>

      <GCCreateDialog key={`create-${createKey}`} open={createOpen} onOpenChange={setCreateOpen} isCoach={isCoach} groupMembers={groupMembers} />
      <GCDetailDialog open={detailOpen} onOpenChange={setDetailOpen} gc={selectedGc} isCoach={isCoach}
        onEdit={handleEdit} onDelete={setDeleteTarget} />
      <GCEditDialog key={`edit-${editKey}`} open={editOpen} onOpenChange={setEditOpen} gc={editingGc} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Godly Council Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 text-white hover:bg-red-700" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MemberLayout>
  )
}
